import { Button, Flex, Stack } from "@mantine/core";
import { IComment, IEditorMetadata, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconSend } from "@tabler/icons-react";
import { EditorState } from "lexical/LexicalEditorState";
import React, { useRef, useState } from "react";
import { SingleComment } from "../../../../../../components";
import RichTextEditor, {
  lexicalStateToPlainText,
  RichEditorHandle,
} from "../../../../../../components/RichTextEditorMantine/RichTextEditor";
import { setHasUnsavedCommentInRedux } from "../../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import { sortArrayByDate } from "../../../../../utils";

const MultiCommentMantine: React.FC<{
  comments: IComment[];
  onAdd: (text: string, metadata: IEditorMetadata) => void;
  onUpdate: (commentId: string, text: string, metadata: IEditorMetadata) => void;
  onDelete: (commentId: string) => void;
  loggedInUser?: IUserDepricated;
  members?: IProjectMemberApiItem[];
  allowDelete?: boolean;
  isLoading: boolean;
}> = ({ comments, onAdd, onUpdate, onDelete, loggedInUser, members, allowDelete = false, isLoading = false }) => {
  const filteredComments = comments.filter((item) => item.deleted === false);
  const sortedComments = sortArrayByDate(filteredComments, "createdDate");

  return (
    <Stack gap="xs">
      <CommentInputSection
        isLoading={isLoading}
        members={members}
        onAddComment={async (text: string, meta: IEditorMetadata) => await onAdd(text, meta)}
      />
      {sortedComments.length > 0 && (
        <Stack gap="lg">
          {sortedComments.map((comment: IComment) => (
            <SingleComment
              key={comment._id}
              comment={comment}
              editEnabled={
                loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                loggedInUser?._id === comment.createdBy
              }
              deleteEnabled={
                loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                loggedInUser?._id === comment.createdBy ||
                allowDelete
              }
              onSave={(text, meta) => onUpdate(comment._id, text, meta)}
              onDelete={() => onDelete(comment._id)}
              members={members}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default MultiCommentMantine;

export const CommentInputSection: React.FC<{
  onAddComment: (text: string, meta: IEditorMetadata) => Promise<void>;
  isLoading: boolean;
  members: IProjectMemberApiItem[];
}> = ({ onAddComment, members, isLoading }) => {
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [plainText, setPlainText] = useState("");
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [hasImages, setHasImages] = useState(false);
  const dispatch = useAppDispatch();
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);

  const add = async () => {
    const plainText = await lexicalStateToPlainText(editorState);

    await onAddComment(JSON.stringify(editorState), { ...editorMetadata, plainText: plainText });
    setEditorState(null);
    setEditorMetadata(null);
    dispatch(setHasUnsavedCommentInRedux(false));
  };

  const handleChange = (editorState, plainText, hasImages) => {
    setEditorState(editorState);
    setPlainText(plainText);
    setHasImages(hasImages);
    if (plainText.length > 0 && hasUnsavedComment !== true) {
      dispatch(setHasUnsavedCommentInRedux(true));
    } else if (plainText.length < 1) {
      dispatch(setHasUnsavedCommentInRedux(false));
    }
  };
  const editorRef = useRef<RichEditorHandle | null>(null);

  return (
    <Stack gap="sm">
      <RichTextEditor
        ref={editorRef}
        disabled={isLoading}
        members={members}
        onChange={handleChange}
        handleMentions={(e) => setEditorMetadata({ ...editorMetadata, mentionIdArray: e })}
      />
      <Flex justify="flex-end" w="100%">
        <Button
          loading={isLoading}
          leftSection={<IconSend size={16} />}
          variant="filled"
          disabled={isLoading || (plainText.length < 1 && hasImages === false)}
          onClick={async () => {
            await add();
            editorRef.current?.reset();
          }}
        >
          Send
        </Button>
      </Flex>
    </Stack>
  );
};
